import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext();

// RestoBoxx palettes as HSL triples; each scheme overrides only the brand variables, surfaces live in index.css.
export const SCHEMES = {
  champagne: {
    label: 'Champagne',
    primary:    '42 45% 68%',   // pale brass — the brand default
    primaryFg:  '40 20% 8%',
    ring:       '42 45% 68%',
    accent:     '42 45% 92%',
    accentDark: '42 25% 18%',
    accentFg:   '38 40% 34%',
  },
  verdigris: {
    label: 'Verdigris',
    primary:    '168 24% 56%',  // aged copper green
    primaryFg:  '170 20% 8%',
    ring:       '168 24% 56%',
    accent:     '168 30% 92%',
    accentDark: '168 18% 17%',
    accentFg:   '168 30% 30%',
  },
  ashrose: {
    label: 'Ash rose',
    primary:    '8 30% 64%',    // dusty rose, greyed right down
    primaryFg:  '10 20% 9%',
    ring:       '8 30% 64%',
    accent:     '8 35% 93%',
    accentDark: '8 20% 19%',
    accentFg:   '8 32% 36%',
  },
  ember: {
    label: 'Ember',
    primary:    '21.2 88.9% 53.9%',  // #F26B21 — the one bright accent, for kitchens that want the heat
    primaryFg:  '0 0% 100%',         // white on the orange: solid buttons read as one block of colour
    ring:       '21.2 88.9% 53.9%',
    accent:     '24 88% 89%',        // pale peach for active rows and soft badges
    accentDark: '20 30% 17%',
    accentFg:   '17 72% 31%',
  },
};

const DEFAULT_SCHEME = 'champagne';
// The scheme is designed dark-first — that's the look, not a preference.
const DEFAULT_DARK = true;

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export const ThemeProvider = ({ children }) => {
  const [dark, setDark] = useState(DEFAULT_DARK);
  const [scheme, setScheme] = useState(DEFAULT_SCHEME);

  useEffect(() => {
    const savedDark = localStorage.getItem('rbx_dark');
    const savedScheme = localStorage.getItem('rbx_scheme');
    if (savedDark != null) setDark(savedDark === 'true');
    if (savedScheme && SCHEMES[savedScheme]) setScheme(savedScheme);
    else {
      // Migration: a saved retired scheme resets to Champagne rather than leaving a palette that no longer exists.
      if (savedScheme) localStorage.setItem('rbx_scheme', DEFAULT_SCHEME);
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add('dark');
    else      root.classList.remove('dark');
    localStorage.setItem('rbx_dark', String(dark));
  }, [dark]);

  useEffect(() => {
    const c = SCHEMES[scheme] || SCHEMES[DEFAULT_SCHEME];
    const root = document.documentElement;
    root.style.setProperty('--primary', c.primary);
    root.style.setProperty('--primary-foreground', c.primaryFg);
    root.style.setProperty('--ring', c.ring);
    root.style.setProperty('--accent', dark ? c.accentDark : c.accent);
    root.style.setProperty('--accent-foreground', c.accentFg);
    localStorage.setItem('rbx_scheme', scheme);
  }, [scheme, dark]);

  const value = useMemo(() => ({
    dark, scheme,
    setDark, setScheme,
    toggleDark: () => setDark((d) => !d),
    schemes: Object.keys(SCHEMES),
    schemeLabels: Object.fromEntries(Object.entries(SCHEMES).map(([k, v]) => [k, v.label])),
  }), [dark, scheme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
